import $ from 'jquery';
import multiselect from '@nobleclem/jquery-multiselect';

// Configuration for filters
const filterConfig = {
    filters: {
        searchInput: 'input[name="provider-search"]',
        providerType: 'input[name="provider-type[]"]',
        presenceType: 'input[name="presence-type[]"]',
        locationSelect: 'select[name="location[]"]',
        dcSelect: 'select[name="dc[]"]',
    },
    endpoint: document.body.classList.contains('single-db-providers') 
        ? '/wp-json/databank/v1/filter-provider/'+providerData.providerID
        : '/wp-json/databank/v1/filter-providers',
    reportEndpoint: '/wp-json/databank/v1/download-csv',
    selectors: {
        resultsContainer: '.results',
        spinnerContainer: '.db-spinner',
        paginationDiv: '.c-pagination',
        paginationLinks: '.c-pagination__link',
        toolbarContainer: '.c-toolbar',
        clearFiltersButton: '.clear-filters',
        providerRange: '.provider-range',
        providerResultsCount: '.provider-results-count',
        downloadReport: '.c-toolbar .download-report',
        container: '.provider-container',
    },
};

// Debounce utility to delay the API calls
function debounce(func, delay) {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), delay);
    };
}

// Function to parse query parameters from URL
function getQueryParams() {
    const params = new URLSearchParams(window.location.search);
    return Array.from(params.entries()).reduce((acc, [key, value]) => {
        if (key.endsWith('[]')) {
            const baseKey = key.slice(0, -2); // Remove "[]"
            acc[baseKey] = acc[baseKey] || [];
            acc[baseKey].push(value);
        } else {
            acc[key] = value;
        }
        return acc;
    }, {});
}

// Function to update query string in the browser without reloading
function updateQueryString(params) {
    const url = new URL(window.location);

    // Clear all existing query parameters
    url.search = '';

    // Add new query parameters
    Object.keys(params).forEach(key => {
        if (Array.isArray(params[key])) {
            params[key].forEach(value => url.searchParams.append(`${key}[]`, value));
        } else if (params[key]) {
            url.searchParams.set(key, params[key]);
        }
    });

    // Update the browser history
    history.pushState({}, '', url);
}

// Function to fetch and display provider data
function filterProviders(updateHistory = true) {
    let searchInput = '';
    let selectedTypes = [];
    let selectedPresenceTypes = [];
    
    // Check if the search input exists
    const $searchInput = $(filterConfig.filters.searchInput);
    if ($searchInput.length) {
        searchInput = $searchInput.val().trim();
    }
    
    // Check if the provider-type checkboxes exist and get the selected values
    const $providerType = $(filterConfig.filters.providerType);
    if ($providerType.length) {
        selectedTypes = $providerType
            .filter(':checked') // Only get the checked checkboxes
            .map(function () {
                return $(this).val(); // Return the value of the checked checkbox
            })
            .get(); // .get() converts the jQuery object to a regular array
    }

    // Check if the presenceType checkboxes exist and get the selected values
    const $presenceType = $(filterConfig.filters.presenceType);
    if ($presenceType.length) {
        selectedPresenceTypes = $presenceType
            .filter(':checked') // Only get the checked checkboxes
            .map(function () {
                return $(this).val(); // Return the value of the checked checkbox
            })
            .get(); // .get() converts the jQuery object to a regular array
    }
    
    
    const params = getQueryParams();
    let currentPage = params.page_num || 1;

    // Build query parameters
    const queryParams = {
        ...(searchInput && { 'provider-search': searchInput }),
        ...(selectedTypes.length > 0 && { 'provider-type': selectedTypes }),
        ...(selectedPresenceTypes.length > 0 && { 'presence-type': selectedPresenceTypes }),
        ...(params.dc && { dc: params.dc }),
        ...(params.locations && { locations: true }),
        page_num: currentPage,
    };

    if (updateHistory) {
        updateQueryString(queryParams); // Update query string
    }

    const queryString = Object.keys(queryParams)
        .map(key => Array.isArray(queryParams[key])
            ? queryParams[key].map(value => `${key}[]=${encodeURIComponent(value)}`).join('&')
            : `${key}=${encodeURIComponent(queryParams[key])}`)
        .join('&');

    // Display loading spinner
    setLoadingState();

    updateClearFiltersVisibility();

    // Fetch data
    fetch(`${filterConfig.endpoint}?${queryString}`)
        .then(response => response.json())
        .then(data => renderResults(data))
        .catch(error => console.error('Error fetching providers:', error));
}

// Utility: Manage loading state
function setLoadingState() {
    const resultsContainer = document.querySelector(filterConfig.selectors.resultsContainer);
    const spinnerContainer = document.querySelector(filterConfig.selectors.spinnerContainer);
    const paginationDiv = document.querySelector(filterConfig.selectors.paginationDiv);
    const toolbarContainer = document.querySelector(filterConfig.selectors.toolbarContainer);
    paginationDiv.innerHTML = '';
    resultsContainer.innerHTML = '';
    toolbarContainer.classList.add('u-hidden');
    
    spinnerContainer.classList.remove('u-hidden');
}

function populateFormFromQuery(params) {
    const dcSelect = $(filterConfig.filters.dcSelect);
    const originalDcOptions = dcSelect.find('option').clone();

    // Populate provider-search input
    if (params['provider-search']) {
        const searchInput = document.querySelector(filterConfig.filters.searchInput);
        if (searchInput) {
            searchInput.value = params['provider-search'];
        }
    }

    // Populate provider-type checkboxes
    if (params['provider-type'] && Array.isArray(params['provider-type'])) {
        params['provider-type'].forEach(type => {
            const checkbox = document.querySelector(`${filterConfig.filters.providerType}[value="${type}"]`);
            if (checkbox) {
                checkbox.checked = true;
            }
        });
    }

    // Populate presence-type checkboxes
    if (params['presence-type'] && Array.isArray(params['presence-type'])) {
        params['presence-type'].forEach(type => {
            const checkbox = document.querySelector(`${filterConfig.filters.presenceType}[value="${type}"]`);
            if (checkbox) {
                checkbox.checked = true;
            }
        });
    }

    // Populate dc[] multi-select
    if (params.dc && Array.isArray(params.dc)) {
        if (dcSelect.length) {
            // Select matching options
            dcSelect.find('option').each(function () {
                const optionValue = $(this).val();
                if (params.dc.includes(optionValue)) {
                    $(this).prop('selected', true);
                } else {
                    $(this).prop('selected', false);
                }
            });

            // Refresh the MultiSelect plugin
            dcSelect.multiselect('reload');
        }
    }

    // Populate location select based on "location=true"
    if (params.locations === 'true') {
        const locationSelect = $(filterConfig.filters.locationSelect);
        if (locationSelect.length && params.dc && Array.isArray(params.dc)) {
            const selectedDcIds = new Set(params.dc.map(id => parseInt(id, 10))); // Normalize DC IDs to integers

            locationSelect.find('option').each(function () {
                const dataDcs = $(this).data('dcs'); // Get the data-dcs attribute as an array
                const hasMatchingDc = dataDcs && dataDcs.some(dc => selectedDcIds.has(dc));

                // Check the option if any of its data-dcs IDs match the query DCs
                $(this).prop('selected', hasMatchingDc);
            });

            // Refresh the MultiSelect plugin
            locationSelect.multiselect('reload');

            const allowedDCs = getSelectedDcs(locationSelect);
            updateSelectOptions(dcSelect, originalDcOptions, allowedDCs, params.dc);
        }
    }

}

// Utility: Render fetched results
function renderResults(data) {
    const resultsContainer = document.querySelector(filterConfig.selectors.resultsContainer);
    const spinnerContainer = document.querySelector(filterConfig.selectors.spinnerContainer);
    const paginationDiv = document.querySelector(filterConfig.selectors.paginationDiv);
    const providerRange = document.querySelector(filterConfig.selectors.providerRange);
    const providerResultsCount = document.querySelector(filterConfig.selectors.providerResultsCount);
    const toolbarContainer = document.querySelector(filterConfig.selectors.toolbarContainer);

    resultsContainer.innerHTML = ''; // Clear previous results
    providerRange.innerHTML = '';
    providerResultsCount.innerHTML = '';

    if (data.items.length > 0) {
        data.items.forEach(item => {
            resultsContainer.innerHTML += item.rendered;
        });
    } else {
        resultsContainer.innerHTML = '<p>'+providerData.noResultsMsg+'</p>';
        toolbarContainer.classList.add('u-hidden');
    }

    if (data.pagination) {
        paginationDiv.innerHTML = data.pagination;
        attachPaginationEvents();
    }

    if (data.results_range && data.items.length > 0) {
        providerRange.innerHTML = data.results_range;
        providerResultsCount.innerHTML = data.total_results;
        toolbarContainer.classList.remove('u-hidden');
    }

    spinnerContainer.classList.add('u-hidden');
}

// Utility: Update and fetch data
function updateParamsAndFetch(params, updateHistory = true) {
    updateQueryString(params);
    filterProviders(updateHistory);
}

// Utility: Update select box options
function updateSelectOptions(select, originalOptions, allowedValues = [], selectedValues = []) {
    select.empty().append(originalOptions); // Restore original options
    if (allowedValues.length > 0) {
        const allowedSet = new Set(allowedValues);
        select.find('option').each(function () {
            $(this).prop('selected', false); // Deselect all options
            if (!allowedSet.has(parseInt($(this).val()))) {
                $(this).remove();
            }else{
                if(selectedValues.length > 0){
                    if(selectedValues.includes($(this).val())){
                        $(this).prop('selected', true);
                    }
                }
            }
        });
    }else{
        select.find('option').each(function () {
            $(this).prop('selected', false); // Deselect all options
        });
    }
    select.multiselect('reload'); // Refresh MultiSelect
}

// Utility: Extract DCs from location[]
function getSelectedDcs(select) {
    const selectedLocations = select.find('option:selected');
    const allowedDCs = new Set();
    selectedLocations.each(function () {
        const dataDcs = $(this).data('dcs');
        if (dataDcs) {
            dataDcs.forEach(dc => allowedDCs.add(dc));
        }
    });
    return [...allowedDCs];
}

// Attach pagination events
function attachPaginationEvents() {
    const paginationLinks = document.querySelectorAll(filterConfig.selectors.paginationLinks);
    paginationLinks.forEach(link => {
        link.addEventListener('click', event => {
            event.preventDefault();
            const page = link.getAttribute('data-page');
            if (!page) return;

            const params = getQueryParams();
            params.page_num = page;
            updateParamsAndFetch(params, false);
            scrollToContainer(); // Scroll to results container
        });
    });
}

// Smooth scroll to container
function scrollToContainer() {
    const container = document.querySelector(filterConfig.selectors.container);
    if (container) {
        const offset = 200;
        const position = container.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: position - offset, behavior: 'smooth' });
    }
}

function handleFilterProviders() {
    const params = getQueryParams(); // Get current query parameters
    params.page_num = 1; // Reset to the first page
    updateQueryString(params); // Update the query string in the URL
    filterProviders(true); // Fetch the results with the updated parameters
    scrollToContainer(); // Optionally scroll to the results container
}

/**
 * Check if any filters are active and toggle the visibility of the clear-filters button.
 */
function updateClearFiltersVisibility() {
    let searchInput = '';
    // Check if the search input exists and get its value if it does
    const $searchInput = $(filterConfig.filters.searchInput);
    if ($searchInput.length) {
        searchInput = $searchInput.val().trim();
    }
    const providerTypeChecked = $(filterConfig.filters.providerType + ':checked').length > 0;
    const presenceTypeChecked = $(filterConfig.filters.presenceType + ':checked').length > 0;
    const locationSelected = $(filterConfig.filters.locationSelect + ' option:selected').length > 0;
    const dcSelected = $(filterConfig.filters.dcSelect + ' option:selected').length > 0;

    const clearFiltersButton = $(filterConfig.selectors.clearFiltersButton);

    if (searchInput || providerTypeChecked || presenceTypeChecked || locationSelected || dcSelected) {
        clearFiltersButton.removeClass('u-hidden');
    } else {
        clearFiltersButton.addClass('u-hidden');
    }
}

/**
 * Clear all filters and update the interface.
 */
function clearFilters(originalDcOptions) {
    // Clear provider-search input
    $(filterConfig.filters.searchInput).val('');

    // Uncheck all provider-type checkboxes
    $(filterConfig.filters.providerType).prop('checked', false);

    // Uncheck all presence-type checkboxes
    $(filterConfig.filters.presenceType).prop('checked', false);

    // Reset location[] and dc[] selects
    const locationSelect = $(filterConfig.filters.locationSelect);
    const dcSelect = $(filterConfig.filters.dcSelect);

    locationSelect.find('option').prop('selected', false);
    locationSelect.multiselect('reload');

    updateSelectOptions(dcSelect, originalDcOptions, []);

    // Update query parameters
    const params = {};
    updateQueryString(params); // Reset query parameters in the URL

    // Fetch the default results
    filterProviders(true);

    // Update clear-filters button visibility
    updateClearFiltersVisibility();
    scrollToContainer();
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', function () {
    const dcSelect = $(filterConfig.filters.dcSelect);
    const locationSelect = $(filterConfig.filters.locationSelect);
    const searchInput = $(filterConfig.filters.searchInput);
    const providerType = $(filterConfig.filters.providerType);
    const presenceType = $(filterConfig.filters.presenceType);
    const downloadReport = $(filterConfig.selectors.downloadReport);
    const restEndpoint = filterConfig.reportEndpoint;
    const clearFiltersEl = $(filterConfig.selectors.clearFiltersButton);
    const originalDcOptions = dcSelect.find('option').clone();

    $('select[multiple]').multiselect({
        search: true,
        selectAll: true,
        selectGroup: true,
        maxHeight: 300,
    });

    locationSelect.on('change', function () {
        const allowedDCs = getSelectedDcs(locationSelect);
        const location_value = allowedDCs.length > 0 ? true : false;
        updateSelectOptions(dcSelect, originalDcOptions, allowedDCs);
        updateParamsAndFetch({ ...getQueryParams(), dc: allowedDCs, page_num: 1, locations: location_value });
    });

    dcSelect.on('change', function () {
        let selectedValues = $(this).val();
        if(selectedValues.length == 0){
            selectedValues = getSelectedDcs(locationSelect);
        }
        updateParamsAndFetch({ ...getQueryParams(), dc: selectedValues, page_num: 1 });
    });

    downloadReport.on('click', function(e) {
        e.preventDefault();
        let params = getQueryParams();

        if(providerData.providerID){
            params.provider_id = providerData.providerID;
        }
        
        const queryString = Object.keys(params)
            .map(key => Array.isArray(params[key])
                ? params[key].map(value => `${key}[]=${encodeURIComponent(value)}`).join('&')
                : `${key}=${encodeURIComponent(params[key])}`)
            .join('&');

        // Build the complete URL
        const fullUrl = queryString ? restEndpoint + '?' + queryString : restEndpoint;

        window.location.href = fullUrl;
    });

    // Attach event listeners for filters
    if (searchInput.length) {
        searchInput.on('input', debounce(handleFilterProviders, 300));
    }

    if (providerType.length) {
        providerType.on('change', handleFilterProviders);
    }

    if (presenceType.length) {
        presenceType.on('change', handleFilterProviders);
    }

    // Attach event listener to clear-filters button
    clearFiltersEl.on('click', function (e) {
        e.preventDefault();
        clearFilters(originalDcOptions);
    });

    const params = getQueryParams();
    populateFormFromQuery(params); // Populate filters with query values
    filterProviders(false);
    attachPaginationEvents();
});